<template>
    <div class="flex flex-col">
        <template v-if="screen.minMD">
            <div class="mb-5">
                <span class="text-lg font-medium">{{ $t('post:editComment') }}</span>
                <span
                    class="icon icon-close h-5 w-5 cursor-pointer float-right text-slate-400 hover:text-slate-600 hidden md:inline-block"
                    @click="closeEdit"
                ></span>
            </div>
            <div class="w-full hidden md:inline-flex items-start">
                <UserAvatar
                    :is-anonymous="isNewIdentityAnonymous"
                    :name="avatar.name"
                    :show-details="false"
                    :animated-avatar="avatar.gamifyAvatar"
                    :show-admin-badge="comment.user ? comment.user.hasAdminBadge : false"
                    :picture="avatar.picture"
                    size="comment"
                    class="mr-4 mt-0.5"
                ></UserAvatar>
                <form
                    class="inline-flex w-full relative"
                    @submit.prevent
                >
                    <textarea
                        ref="textarea"
                        v-model="postComment.text"
                        v-dynamic-height
                        style="max-height: 160px"
                        :placeholder="$t('post:Comment')"
                        class="w-full py-1 px-2 pr-32 rounded break-words whitespace-pre-wrap resize-none bg-slate-50 min-h-[2.5rem]"
                        @click.prevent="openPopup"
                        @input="getOverflown"
                    >
                    </textarea>
                    <div
                        class="absolute flex self-end right-0 text-slate-400 mb-2 mr-3"
                        :class="{ 'mr-8': isTextareaOverflown }"
                    >
                        <Btn
                            v-if="isAnonymityEnabled"
                            v-tooltip="$t('post:Comment anonymously')"
                            data-testid="post-comment-edit_btn-anonymous"
                            variant="tonal"
                            :class="['mr-3', user?.is_premium ? 'text-slate-400' : 'text-purple-500']"
                            :content-color="user?.is_premium ? 'slate-600' : 'purple-500'"
                            active-color="blue-600"
                            active-content-color="blue-600"
                            icon="incognito"
                            size="xxs"
                            :active="postComment.isAnonymous"
                            color-on-touch
                            @click.prevent="toggleAnonymousComment"
                        />
                        <Btn
                            v-tooltip="$t('common:tooltip.camera')"
                            data-testid="post-comment-edit_btn-image"
                            variant="tonal"
                            class="mr-3 text-slate-400"
                            content-color="slate-600"
                            icon="camera"
                            size="xxs"
                            color-on-touch
                            @click.prevent.stop="editWithPhoto = !editWithPhoto"
                        />

                        <Btn
                            v-tooltip="$t('common:tooltip.send')"
                            data-testid="post-comment-edit_btn-send"
                            variant="tonal"
                            class="text-slate-400"
                            content-color="slate-600"
                            icon="send"
                            size="xxs"
                            :disabled="postIsProcessing"
                            color-on-touch
                            @click="submitComment"
                        />
                    </div>
                </form>
            </div>
            <transition name="slide-open-y">
                <div
                    v-if="editWithPhoto"
                    style="max-height: 130px"
                >
                    <!-- eslint-disable vue/no-bare-strings-in-template -->
                    <PostPhotoUpload
                        key="photo"
                        :files="comment.files"
                        class="pt-5 px-3 pb-3 -mx-3 -mb-3"
                        spacer-left-width="3rem"
                        title="photo"
                        @upload-start="isUploading = true"
                        @upload-end="isUploading = false"
                        @uploaded="updateCommentAddImage"
                        @delete="updateCommentDeleteImage"
                    ></PostPhotoUpload>
                    <!-- eslint-disable vue/no-bare-strings-in-template -->
                </div>
            </transition>
        </template>
        <template v-else>
            <transition name="bounce">
                <form
                    v-show="popupIsOpen"
                    class="fixed bg-white w-full h-full inset-0 z-50"
                    @submit.prevent
                >
                    <div class="border-b inline-flex w-full py-2 container">
                        <i
                            class="min-w-6 icon icon-close mr-4 my-2 cursor-pointer text-slate-400 rounded hover:bg-slate-200 hover:text-slate-600"
                            @click="closeEdit"
                        ></i>
                        <UserAvatar
                            :animated-avatar="avatar.gamifyAvatar"
                            :picture="avatar.picture"
                            :show-details="false"
                            :is-anonymous="isNewIdentityAnonymous"
                            :show-admin-badge="comment.user ? comment.user.hasAdminBadge : false"
                            class="mr-2 h-10 w-10"
                        ></UserAvatar>
                        <span class="truncated leading-10 font-medium mr-auto">{{
                            isNewIdentityAnonymous ? $t('Comment anonymously') : $t('Comment as', { name: avatar.name })
                        }}</span>
                        <BtnCta
                            data-testid="post-comment-edit_btn-send"
                            icon="send"
                            class="ml-2"
                            size="sm"
                            :disabled="postIsProcessing || isUploading"
                            @click="submitComment"
                        />
                    </div>
                    <textarea
                        ref="textareaMobile"
                        v-model="postComment.text"
                        :placeholder="$t('post:Comment')"
                        class="bg-white w-full overlay py-5 container border-none border-b resize-none outline-none h-full"
                        style="height: calc(100% - 12rem)"
                        required
                    ></textarea>
                    <div class="absolute left-0 bottom-0 w-full inline-flex flex-col">
                        <div
                            v-if="editWithPhoto"
                            class="whitespace-nowrap overflow-x-scroll bg-white border-t pl-0 p-4"
                        >
                            <!-- eslint-disable vue/no-bare-strings-in-template -->
                            <PostPhotoUpload
                                key="photo"
                                class="md:mt-5 pl-4"
                                :files="comment.files"
                                title="photo"
                                @upload-start="isUploading = true"
                                @upload-end="isUploading = false"
                                @uploaded="updateCommentAddImage"
                                @delete="updateCommentDeleteImage"
                            ></PostPhotoUpload>
                            <!-- eslint-enable vue/no-bare-strings-in-template -->
                        </div>
                        <div :class="['inline-flex border-t py-4 container', isAnonymityEnabled ? '' : 'justify-end']">
                            <Btn
                                v-if="isAnonymityEnabled"
                                data-testid="post-comment-edit_btn-anonymous"
                                variant="tonal"
                                :class="['ml-auto mr-2', user?.is_premium ? 'text-slate-400' : 'text-purple-500']"
                                :content-color="user?.is_premium ? 'slate-600' : 'purple-500'"
                                active-color="blue-600"
                                active-content-color="blue-600"
                                icon="incognito"
                                size="sm"
                                :active="postComment.isAnonymous"
                                color-on-touch
                                @click.prevent="postComment.isAnonymous = !postComment.isAnonymous"
                            />
                            <Btn
                                data-testid="post-comment-edit_btn-image"
                                variant="tonal"
                                class="btn-photo mr-2 h-10 text-slate-400"
                                content-color="slate-600"
                                active-color="blue-600"
                                active-content-color="blue-600"
                                icon="camera"
                                size="sm"
                                :active="editWithPhoto"
                                color-on-touch
                                @click.prevent.stop="editWithPhoto = !editWithPhoto"
                            />
                        </div>
                    </div>
                </form>
            </transition>
        </template>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import route from '@/router/route';
// eslint-disable-next-line import/no-restricted-paths
import { backend } from '@/api/backend';
import { BtnSize } from '@components/Btn';
import PostPhotoUpload from '@components/PostItem/PostPhotoUpload.vue';
import { featureIsEnabled } from '@/modules/features';
import { removeFromArray } from '@/helpers/array';
import { isOverflown } from '@/helpers/dom';
import { showSubscriptionShop } from '@helpers/subscription';
import { decodeEntities } from '@/helpers/string';

export default {
    name: 'PostCommentEdit',

    components: {
        PostPhotoUpload,
    },

    props: {
        postId: {
            type: Number,
            required: true,
        },
        comment: {
            type: Object,
            required: true,
        },
        context: {
            type: String,
            required: true,
        },
        contextId: {
            type: Number,
            required: true,
        },
    },
    emits: ['close-comment-edit'],

    i18nOptions: {
        namespaces: ['common', 'post'],
    },

    data() {
        return {
            BtnSize,
            postComment: {
                text: decodeEntities(this.comment.content),
                isAnonymous: this.comment.isAnonymous,
                anonymousUser: this.comment.anonymousUser,
                user: this.comment.user,
                uploadedFiles: [],
                deleteFiles: [],
            },
            popupIsOpen: false,
            editWithPhoto: this.comment.files.length > 0,
            postIsProcessing: false,
            isUploading: false,
            isAnonymous: this.comment.isAnonymous,
            isTextareaOverflown: false,
        };
    },

    computed: {
        ...mapGetters('auth', ['isLoggedIn', 'userPicture']),
        ...mapGetters('ui', ['screen', 'module']),
        ...mapGetters('user', ['user']),
        isAnonymityEnabled() {
            return featureIsEnabled('anonymityIdentity') && this.user?.is_premium_system_enabled;
        },
        isIdentityChanged() {
            const commentAnonymous = this.postComment.isAnonymous;
            return this.isAnonymous !== commentAnonymous;
        },
        isNewIdentityAnonymous() {
            return !this.isAnonymous && this.isIdentityChanged;
        },
        isNewIdentityUser() {
            return this.isAnonymous && this.isIdentityChanged;
        },
        avatar() {
            if (this.isNewIdentityUser) {
                // comment was anonymous, so retrieve current user
                return {
                    name: this.user.username,
                    picture: this.userPicture,
                    gamifyAvatar: this.user.gamify_avatar_url,
                };
            }

            // otherwise, user data is stored in comment
            const details = this.postComment;
            if (details.isAnonymous) {
                return {
                    name: details.anonymousUser ? details.anonymousUser.name : null,
                    picture: details.anonymousUser ? `/${details.anonymousUser.icon}` : null,
                    gamifyAvatar: null,
                    hasAdminBadge: false,
                };
            }

            return {
                name: details.user.name,
                picture: details.user.profilePictureUrl ?? details.user.avatarPictureUrl,
                hasAdminBadge: details.user.hasAdminBadge,
                gamifyAvatar: this.user.gamify_avatar_url,
            };
        },
    },

    methods: {
        toggleAnonymousComment() {
            if (this.user?.is_premium_system_enabled && !this.user?.is_premium) {
                showSubscriptionShop('anonymous_posting_button_click');
                return;
            }
            this.postComment.isAnonymous = !this.postComment.isAnonymous;
        },
        focusCommentInput() {
            if (this.screen.maxSM) {
                this.openPopup();
                return;
            }
            this.$refs.textarea.focus();
            const event = new Event('input');
            this.$refs.textarea.dispatchEvent(event);
        },
        async getOverflown() {
            await this.$nextTick();
            this.isTextareaOverflown = isOverflown(this.$refs.textarea);
        },
        openPopup() {
            if (this.screen.maxSM) {
                this.popupIsOpen = true;
                document.body.classList.add('overflow-hidden');
                this.$nextTick(() => this.$refs.textareaMobile.focus());
            }
        },
        closeEdit() {
            this.popupIsOpen = false;
            document.body.classList.remove('overflow-hidden');
            this.$emit('close-comment-edit', this.comment.id);
        },
        submitComment() {
            if (this.isIdentityChanged) {
                this.confirmIdentityChange();
                return;
            }
            this.update();
        },
        async confirmIdentityChange() {
            this.closeEdit();

            const result = await this.$modal.open(
                defineAsyncComponent(() => import('@/components/ModalConfirmPostIdentityChange')),
                {
                    isAnonymous: this.isAnonymous,
                    profileImage: this.userPicture,
                    gamifyAvatar: this.user.gamify_avatar_url,
                },
            );

            switch (result) {
                case true:
                    return this.update();
                case false:
                    return this.onIdentityChangeDeclined();
                default:
                    return null;
            }
        },
        async onIdentityChangeDeclined() {
            this.postComment.isAnonymous = !this.postComment.isAnonymous;
            await this.$nextTick();
            this.update();
        },
        async update() {
            if (this.postIsProcessing || this.isUploading) return;
            this.postIsProcessing = true;

            const checkData = {
                type: 'answer',
                questionId: this.postId,
                answerId: this.comment.id,
                postAnonymous: this.postComment.isAnonymous,
                isFormerKEorCM: false,
                isEdit: true,
            };

            const comment = {
                [`${this.context}id`]: this.contextId,
                documentPage: false,
                type: this.context,
                postAnonymous: this.postComment.isAnonymous,
                postAnonymousType: this.postComment.anonymousUser ? this.postComment.anonymousUser.typeId : null,
                answerId: this.comment.id,
                text: this.postComment.text,
                files: this.postComment.uploadedFiles,
                deleteFiles: this.postComment.deleteFiles,
            };

            try {
                await backend.post(route('post.anonymous.identities.check', { context: this.context }), checkData);
            } catch (error) {
                this.postIsProcessing = false;
                this.onError(error);
                return;
            }

            const { isIdentityChanged } = this;

            await this.$store
                .dispatch(`${this.module}/editComment`, {
                    context: this.context,
                    comment,
                })
                .then((response) => this.onEdited(response, isIdentityChanged))
                .catch(this.onError)
                .finally(() => {
                    this.postIsProcessing = false;
                });
        },
        updateCommentAddImage(file) {
            this.postComment.uploadedFiles.push(file);
        },
        updateCommentDeleteImage(file) {
            removeFromArray(file, this.postComment.uploadedFiles, (f) => f.id === file.id);
            this.postComment.deleteFiles.push(file);
        },
        onEdited(response, isIdentityChanged) {
            if (isIdentityChanged) {
                this.$store.dispatch(`${this.module}/propagateIdentityChange`, {
                    postId: this.postId,
                    user: response.comment.user || response.comment.anonymousUser,
                    isAnonymous: response.comment.isAnonymous,
                });
            }
            this.closeEdit();
        },
        onError(error) {
            this.$store.dispatch('flashMessages/showError', {
                content: error.response?.data?.message || this.$t('errors.requestError'),
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.truncated {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
